import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AdminEventDetailDTO, EventPositionDTO } from "./types";
import { Position } from "@/services/positionService";
import { partnerService } from "@/services/partnerService";
import { PartnerDto } from "@/types/partner";
import { axiosInstance } from "@/pages/auth/auth.context";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { formatTime12Hour } from "@/utils/timeFormatUtils";

interface EventWithPositions extends AdminEventDetailDTO {
  eventPositions: EventPositionDTO[];
}

/** One priced line on a bid, as returned by GET event/{id}/bid-line-items. */
interface BidLineItem {
  name: string;
  details: string | null;
  quantity: number | null;
  hourlyRate: number | null;
  hours: number | null;
  amount: number;
}

/**
 * What the tables on a bid add up to. `tablesMissingCapacity` counts tables
 * whose position has no guest capacity set, which makes `guestsAccommodated` a
 * floor rather than the real number.
 */
interface BidTableTotals {
  tables: number;
  guestsAccommodated: number;
  tablesMissingCapacity: number;
}

interface BidLineItems {
  lineItems: BidLineItem[];
  total: number;
  tableTotals: BidTableTotals | null;
}

interface EventDetailsPrintProps {
  event?: EventWithPositions;
  availablePositions?: Position[];
  mode?: "event" | "bid";
}

const EventDetailsPrint: React.FC<EventDetailsPrintProps> = ({
  event: eventProp,
  availablePositions,
  mode = "event",
}) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const [partnerInfo, setPartnerInfo] = useState<PartnerDto | null>(null);
  const [isLoadingPartner, setIsLoadingPartner] = useState(false);
  const [event, setEvent] = useState<EventWithPositions | null>(
    eventProp || null,
  );
  const [loading, setLoading] = useState(!eventProp);
  const [bidStatus, setBidStatus] = useState<string>("");
  const [bidLineItems, setBidLineItems] = useState<BidLineItems | null>(null);

  useEffect(() => {
    const fetchEventData = async () => {
      if (eventProp || !id) return;

      setLoading(true);
      try {
        const endpoint = mode === "bid" ? `event/bid/${id}` : `event/${id}`;
        const response = await axiosInstance.get(endpoint);
        setEvent(response.data);

        if (mode === "bid") {
          try {
            const statusResponse = await axiosInstance.get(
              `event/bid/${id}/status`,
            );
            if (statusResponse.data?.status) {
              setBidStatus(statusResponse.data.status);
            }
          } catch (error) {
            console.error("Error fetching bid status:", error);
          }

          // The tables, quantities and pricing the client is being quoted.
          try {
            const lineItemResponse = await axiosInstance.get(
              `event/${id}/bid-line-items`,
            );
            setBidLineItems(lineItemResponse.data);
          } catch (error) {
            console.error("Error fetching bid line items:", error);
          }
        }
      } catch (error) {
        console.error("Error fetching event data:", error);
        toast({
          title: "Error",
          description: `Failed to load ${mode} details`,
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchEventData();
  }, [id, eventProp, mode, toast]);

  // Auto-print for bid mode
  useEffect(() => {
    if (mode === "bid" && event && !loading) {
      const timer = setTimeout(() => {
        window.print();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [mode, event, loading]);

  useEffect(() => {
    const fetchPartnerInfo = async () => {
      if (!event?.referralCode || event.referralCode.trim() === "") {
        return;
      }

      try {
        setIsLoadingPartner(true);
        const validation = await partnerService.validateReferralCode(
          event.referralCode.trim(),
        );

        if (validation.valid && validation.partner) {
          setPartnerInfo(validation.partner);
        }
      } catch (error) {
        console.error("Error fetching partner info:", error);
      } finally {
        setIsLoadingPartner(false);
      }
    };

    fetchPartnerInfo();
  }, [event?.referralCode]);

  // Format currency
  /** Table cell styling for the printed sheet. */
  const printCell = (
    align: "left" | "right",
    heading = false,
  ): React.CSSProperties => ({
    border: "1px solid #d1d5db",
    padding: "0.5rem 0.75rem",
    textAlign: align,
    fontWeight: heading ? "bold" : "normal",
    whiteSpace: align === "right" ? "nowrap" : "normal",
  });

  const formatCurrency = (amount: number) => {
    if (!amount) return "$0.00";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  if (loading) {
    return (
      <div
        style={{
          background: "#fff",
          color: "#000",
          padding: "2rem",
          fontFamily: "Arial, sans-serif",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          Loading {mode} details...
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div
        style={{
          background: "#fff",
          color: "#000",
          padding: "2rem",
          fontFamily: "Arial, sans-serif",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          {mode === "bid" ? "Bid" : "Event"} not found
        </div>
      </div>
    );
  }
  return (
    <div
      className="print-sheet"
      style={{
        background: "#fff",
        color: "#000",
        padding: "2rem",
        fontFamily: "Arial, sans-serif",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h1
          style={{ fontSize: "2rem", fontWeight: "bold", marginBottom: "1rem" }}
        >
          {mode === "bid" ? "Bid Details" : "Event Details"}
        </h1>
        <div className="no-print" style={{ display: "flex", gap: "0.5rem" }}>
          <button
            style={{
              padding: "0.5rem 1rem",
              background: "hsl(var(--primary))",
              color: "hsl(var(--primary-foreground))",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
            onClick={() => window.print()}
          >
            Print
          </button>
          <Button
            style={{
              padding: "0.5rem 1rem",
              background: "hsl(var(--primary))",
              color: "hsl(var(--primary-foreground))",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        </div>
      </div>

      {/* Status Badge for Bid Mode */}
      {mode === "bid" && bidStatus && (
        <div style={{ marginBottom: "2rem" }}>
          <span
            style={{
              background: "#f3f4f6",
              color: "#374151",
              padding: "0.5rem 1rem",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
              fontSize: "1rem",
              fontWeight: "bold",
            }}
          >
            Status: {bidStatus}
          </span>
        </div>
      )}

      <section style={{ marginBottom: "2rem" }}>
        <div>
          <strong>Event ID:</strong> {event.eventId}
        </div>
        <div>
          <strong>Company Name:</strong> {event.companyName}
        </div>
        <div>
          <strong>Contact Name:</strong> {event.contactName}
        </div>
        <div>
          <strong>Email:</strong> {event.email}
        </div>
        <div>
          <strong>Phone Number:</strong> {event.phoneNumber}
        </div>
        <div>
          <strong>Address:</strong> {event.address}
        </div>
        <div>
          <strong>City:</strong> {event.city}
        </div>
        <div>
          <strong>Location Name:</strong> {event.nameOfLocation}
        </div>
        <div>
          <strong>Date:</strong>{" "}
          {event.startTime
            ? new Date(event.startTime).toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })
            : "Not specified"}
        </div>
        <div>
          <strong>Duration:</strong>{" "}
          {event.startTime && event.eventLength
            ? (() => {
                const startTime = new Date(event.startTime);
                const endTime = new Date(
                  startTime.getTime() + event.eventLength * 60 * 60 * 1000,
                );
                const startTimeStr = startTime.toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                });
                const endTimeStr = endTime.toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                });
                return `${startTimeStr} - ${endTimeStr} (${
                  event.eventLength
                } hour${event.eventLength !== 1 ? "s" : ""})`;
              })()
            : "Not specified"}
        </div>
        <div>
          <strong>Attendees:</strong> {event.attendees}
        </div>
        <div>
          <strong>Delivery Info:</strong> {event.deliveryInfo}
        </div>
        <div>
          <strong>Interested Games:</strong> {event.interestedGames}
        </div>
        <div>
          <strong>Has Raffle:</strong> {event.hasRaffle}
        </div>
        <div>
          <strong>Want Us To Do Raffle:</strong> {event.wantUsToDoRaffle}
        </div>
        <div>
          <strong>Food Info:</strong> {event.foodInfo}
        </div>
        <div>
          <strong>Firmness Level:</strong> {event.firmnessLevel}
        </div>
        <div>
          <strong>Dealer Special Instructions:</strong>{" "}
          {event.dealerSpecialInstructions}
        </div>
        <div>
          <strong>Setup Time:</strong>{" "}
          {event.setupTime
            ? formatTime12Hour(event.setupTime)
            : "Not specified"}
        </div>
        <div>
          <strong>Parking Info:</strong> {event.parkingInfo || "Not specified"}
        </div>
        <div>
          <strong>Extra Pay Per Dealer:</strong>{" "}
          {formatCurrency(event.extraPayPerDealer)}
        </div>
        <div>
          <strong>Additional Info:</strong> {event.additionalInfo}
        </div>
        <div>
          <strong>Additional Fee:</strong> {formatCurrency(event.additionalFee)}
        </div>
        <div>
          <strong>Discount Amount:</strong>{" "}
          {formatCurrency(event.discountAmount)}
        </div>
        <div>
          <strong>Split Payment:</strong> {event.splitPayment ? "Yes" : "No"}
        </div>
      </section>

      {event.referralCode && event.referralCode.trim() !== "" && (
        <section style={{ marginBottom: "2rem" }}>
          <h2 style={{ fontSize: "1.2rem", fontWeight: "bold" }}>
            Referral Information
          </h2>
          <div>
            <strong>Referral Code:</strong> {event.referralCode}
          </div>
          {isLoadingPartner ? (
            <div>Loading partner information...</div>
          ) : partnerInfo ? (
            <div>
              <strong>Referral Company:</strong> {partnerInfo.companyName}
            </div>
          ) : null}
        </section>
      )}

      {/* Requested Positions for Bid Mode */}
      {mode === "bid" && bidLineItems && bidLineItems.lineItems.length > 0 && (
        <section style={{ marginBottom: "2rem" }}>
          <h2 style={{ fontSize: "1.2rem", fontWeight: "bold" }}>
            Tables and Services
          </h2>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              marginTop: "1rem",
              fontSize: "0.9rem",
            }}
          >
            <thead>
              <tr style={{ background: "#f3f4f6" }}>
                <th style={printCell("left", true)}>Item</th>
                <th style={printCell("right", true)}>Quantity</th>
                <th style={printCell("right", true)}>Rate</th>
                <th style={printCell("right", true)}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {bidLineItems.lineItems.map((line) => (
                <tr key={line.name}>
                  <td style={printCell("left")}>
                    {line.name}
                    {line.details && (
                      <div style={{ color: "#6b7280", fontSize: "0.8rem" }}>
                        {line.details}
                      </div>
                    )}
                  </td>
                  <td style={printCell("right")}>
                    {line.quantity ?? "\u2014"}
                  </td>
                  <td style={printCell("right")}>
                    {line.hourlyRate !== null
                      ? `${formatCurrency(line.hourlyRate)} per hour`
                      : "\u2014"}
                  </td>
                  <td style={printCell("right")}>
                    {formatCurrency(line.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td
                  colSpan={3}
                  style={{
                    ...printCell("right", true),
                    borderTop: "2px solid #374151",
                  }}
                >
                  Total
                </td>
                <td
                  style={{
                    ...printCell("right", true),
                    borderTop: "2px solid #374151",
                  }}
                >
                  {formatCurrency(bidLineItems.total)}
                </td>
              </tr>
            </tfoot>
          </table>

          {bidLineItems.tableTotals && (
            <div style={{ marginTop: "1rem" }}>
              <div>
                <strong>Total Tables:</strong> {bidLineItems.tableTotals.tables}
              </div>
              <div>
                <strong>Guests Accommodated:</strong>{" "}
                {bidLineItems.tableTotals.guestsAccommodated}
                {bidLineItems.tableTotals.tablesMissingCapacity > 0 &&
                  " or more"}
              </div>
              {/* Without this the headcount silently understates the party. */}
              {bidLineItems.tableTotals.tablesMissingCapacity > 0 && (
                <div style={{ color: "#6b7280", fontSize: "0.85rem" }}>
                  {bidLineItems.tableTotals.tablesMissingCapacity} table
                  {bidLineItems.tableTotals.tablesMissingCapacity === 1
                    ? " has"
                    : "s have"}{" "}
                  no guest capacity set, so the count above is a minimum.
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {mode === "bid" &&
        bidLineItems &&
        bidLineItems.lineItems.length === 0 && (
          <section style={{ marginBottom: "2rem" }}>
            <h2 style={{ fontSize: "1.2rem", fontWeight: "bold" }}>
              Tables and Services
            </h2>
            <p style={{ color: "#6b7280" }}>
              No tables have been priced for this bid yet.
            </p>
          </section>
        )}

      {/* Event Positions Table for Event Mode */}
      {mode === "event" &&
        event.eventPositions &&
        event.eventPositions.length > 0 && (
          <section style={{ marginBottom: "2rem" }}>
            <h2 style={{ fontSize: "1.2rem", fontWeight: "bold" }}>
              Event Positions
            </h2>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th
                    style={{
                      borderBottom: "1px solid #000",
                      textAlign: "left",
                    }}
                  >
                    Table Name
                  </th>
                  <th
                    style={{
                      borderBottom: "1px solid #000",
                      textAlign: "left",
                    }}
                  >
                    Table Quantity
                  </th>
                  <th
                    style={{
                      borderBottom: "1px solid #000",
                      textAlign: "left",
                    }}
                  >
                    Total Employees Required
                  </th>
                  <th
                    style={{
                      borderBottom: "1px solid #000",
                      textAlign: "left",
                    }}
                  >
                    Employee Pay
                  </th>
                  <th
                    style={{
                      borderBottom: "1px solid #000",
                      textAlign: "left",
                    }}
                  >
                    Assigned Staff
                  </th>
                </tr>
              </thead>
              <tbody>
                {event.eventPositions.map((pos) => (
                  <tr key={pos.eventPositionId}>
                    <td
                      style={{
                        borderBottom: "1px solid #ccc",
                        padding: "0.5rem",
                      }}
                    >
                      {pos.tableName}
                    </td>
                    <td
                      style={{
                        borderBottom: "1px solid #ccc",
                        padding: "0.5rem",
                      }}
                    >
                      {pos.quantity}
                    </td>
                    <td
                      style={{
                        borderBottom: "1px solid #ccc",
                        padding: "0.5rem",
                      }}
                    >
                      {pos.employeesRequired * pos.quantity}
                    </td>
                    <td
                      style={{
                        borderBottom: "1px solid #ccc",
                        padding: "0.5rem",
                      }}
                    >
                      ${pos.employeePay}
                    </td>
                    <td
                      style={{
                        borderBottom: "1px solid #ccc",
                        padding: "0.5rem",
                      }}
                    >
                      {pos.signedUpUsers.length > 0
                        ? pos.signedUpUsers
                            .map(
                              (staff) => `${staff.firstName} ${staff.lastName}`,
                            )
                            .join(", ")
                        : "Unassigned"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
    </div>
  );
};

export default EventDetailsPrint;
